import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { fromUnixTime } from 'date-fns';

import Button from '../Button';
import { Sizes as BUTTON_SIZES } from '../Button/Button';

import { formatDate } from '../../utils/date';

const DATE_FORMAT = 'd MMM yyyy г., HH:mm';

const emailsRenderer = emails =>
  emails.map(({ name, email }) => (
    <StyledEmail key={email}>{name ? `${name} <${email}>` : email}</StyledEmail>
  ));

const EmailPreview = React.memo(
  ({ body, createdAt, downloadLink, from, subject, to, ...rest }) => {
    const date = fromUnixTime(createdAt);
    const createDateLabel = formatDate(date, DATE_FORMAT);

    const handlePrint = useCallback(() => {
      window.print();
    });

    return (
      <StyledEmailPreview {...rest}>
        <StyledPrintable>
          <StyledFields>
            <StyledWrapper>
              <StyledField>
                <StyledFieldLabel>Создано:</StyledFieldLabel>
                <StyledFieldValue>{createDateLabel}</StyledFieldValue>
              </StyledField>
              <StyledField>
                <StyledFieldLabel>От:</StyledFieldLabel>
                <StyledFieldValue>{emailsRenderer(from)}</StyledFieldValue>
              </StyledField>
              <StyledField>
                <StyledFieldLabel>Кому:</StyledFieldLabel>
                <StyledFieldValue>{emailsRenderer(to)}</StyledFieldValue>
              </StyledField>
              <StyledField>
                <StyledFieldLabel>Тема:</StyledFieldLabel>
                <StyledFieldValue>{subject}</StyledFieldValue>
              </StyledField>
            </StyledWrapper>
          </StyledFields>
          <StyledWrapper>
            <StyledBody dangerouslySetInnerHTML={{ __html: body }} />
          </StyledWrapper>
        </StyledPrintable>
        <StyledActions>
          <StyledPrintButton onClick={handlePrint} size={BUTTON_SIZES.TINY}>
            Распечатать...
          </StyledPrintButton>
          {downloadLink && (
            <StyledSaveLink href={downloadLink} target="_blank">
              Сохранить PDF
            </StyledSaveLink>
          )}
        </StyledActions>
      </StyledEmailPreview>
    );
  }
);

const emailProptype = PropTypes.shape({
  name: PropTypes.string,
  email: PropTypes.string,
});

EmailPreview.displayName = 'EmailPreview';
EmailPreview.propTypes = {
  className: PropTypes.string,
  id: PropTypes.number,
  subject: PropTypes.string,
  from: PropTypes.arrayOf(emailProptype),
  to: PropTypes.arrayOf(emailProptype),
  body: PropTypes.string,
  createdAt: PropTypes.number,
  downloadLink: PropTypes.string,
  link: PropTypes.string,
};

EmailPreview.defaultProps = {
  className: '',
  id: null,
  subject: null,
  from: [],
  to: [],
  body: null,
  createdAt: null,
  downloadLink: null,
  link: null,
};

const StyledWrapper = styled.div`
  max-width: 1160px;
  padding: 16px 20px;
  margin: 0 auto;
`;

const StyledPrintable = styled.div``;

const StyledEmail = styled.span`
  margin-right: 15px;
`;

const StyledEmailPreview = styled.div`
  font-size: 14px;
`;

const StyledFields = styled.div`
  background: white;
  width: 100%;
`;

const StyledField = styled.div`
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #e6e9ee;
`;

const StyledFieldLabel = styled.div`
  width: 300px;
  font-weight: bold;
`;

const StyledFieldValue = styled.div`
  flex: 1;
  display: flex;
  flex-flow: wrap;
`;

const StyledBody = styled.div`
  padding: 25px 23px;
  overflow-x: auto;
  overflow-y: hidden;
  color: #4d4d4d;
  line-height: 18px;
  width: 100%;
  background: white;
  border-radius: 4px;
`;

const StyledActions = styled(StyledWrapper)`
  display: flex;
  padding: 0 20px 16px;

  @media print {
    display: none;
  }
`;

const StyledPrintButton = styled(Button)`
  width: 145px;
  margin-right: 8px;
  border: none;
  font-weight: bold;
  height: 36px;
  line-height: 36px;
  background: #007aff;
  color: white;

  &:hover {
    color: white;
  }
`;

const StyledSaveLink = styled.a`
  width: 145px;
  border: 1px solid;
  text-align: center;
  height: 36px;
  text-decoration: none;
  line-height: 36px;
  box-sizing: border-box;
  color: #007aff;
  border-color: #007aff;
  border-radius: 4px;
`;

export default EmailPreview;
